import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Anchor, Activity, FileCheck, ArrowUpRight, BarChart, Map, Shield, Eye, Layers, Database } from 'lucide-react';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import { ReadingHeader } from '@/components/gol/ReadingHeader';
import { ReadingNext } from '@/components/gol/ReadingNext';

const BlueCapital = () => {
  return (
    <div data-testid="blue-capital-page" className="reading-page min-h-screen bg-abyss-950 text-sand-50 font-sans selection:bg-teal-400/30 overflow-x-hidden relative">
      <InteractiveBackground />

      <div className="relative z-10 pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-24">
            
            {/* Header */}
            <ReadingHeader id="blue-capital" category="Natural capital" title="Follow the evidence beneath the surface." intro="Marine and coastal systems connect carbon, fisheries, biodiversity and protection. This work makes those dependencies inspectable, grounding each claim in place and evidence." />

            {/* The Challenge */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">The Challenge</h2>
                </div>
                <div className="md:col-span-8 prose prose-invert prose-lg text-sand-50/80 font-light leading-relaxed">
                    <p>
                        A coastal ecosystem can support fisheries, store carbon and protect communities all at once. Those benefits are connected, but each one calls for its own observations, its own assumptions, its own assessment.
                    </p>
                    <p>
                        A useful assessment traces those services back to the places and processes that sustain them, and makes the gaps visible before an ecological signal becomes a financial claim.
                    </p>
                </div>
            </div>

            {/* The Approach */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">The Approach</h2>
                </div>
                <div className="md:col-span-8 space-y-12">
                    <p className="text-lg text-sand-50/80 font-light">
                        I build spatial evidence frameworks that connect ecosystem function to a bounded assessment question, carrying uncertainty and governance conditions through every step of the analysis.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                        <FeatureCard 
                            icon={Map}
                            title="Spatially Explicit"
                            desc="Valuation across thousands of planning units as well as regional averages."
                        />
                        <FeatureCard 
                            icon={FileCheck}
                            title="Standard Aligned"
                            desc="Evidence organized with reference to TNFD, SEEA and relevant carbon-standard requirements."
                        />
                        <FeatureCard 
                            icon={Activity}
                            title="Uncertainty Quantification"
                            desc="Explicit assumptions and uncertainty bounds appropriate to the available evidence."
                        />
                         <FeatureCard 
                            icon={Layers}
                            title="Service Stacking"
                            desc="Integrating provisioning, regulating, cultural, and supporting services."
                        />
                    </div>
                </div>
            </div>

            {/* DIFFERENTIATOR BANNER */}
            <div className="py-8">
                <div className="p-8 bg-teal-900/5 border-l-2 border-teal-500 rounded-r-sm backdrop-blur-sm">
                    <p className="text-lg md:text-xl font-serif text-sand-50/90 italic leading-relaxed">
                        A useful assessment lets the next reviewer follow the same evidence and understand its limits.
                    </p>
                </div>
            </div>

            {/* The Gulf Proof */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <div className="sticky top-32">
                        <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 mb-2">The Gulf Proof</h2>
                        <span className="text-[10px] text-sand-50/40 uppercase tracking-widest">Live Demonstration</span>
                    </div>
                </div>
                <div className="md:col-span-8 space-y-8">
                    <p className="text-lg text-sand-50/80 font-light">
                        One governed reasoning graph carries the translation stack end to end. A place anchors evidence, sources and caveats; a bounded decision follows; and observed ecological evidence can challenge the model in public.
                    </p>

                    <div className="bg-abyss-900/50 border border-abyss-800 rounded-lg p-8 space-y-6 hover:border-teal-500/30 transition-colors group">
                        <div className="flex items-center gap-3 text-teal-400 mb-2">
                            <Layers size={20} />
                            <h3 className="text-sm font-mono uppercase tracking-widest">BlueGraph</h3>
                        </div>
                        <div className="text-2xl font-serif text-sand-50">Spatial Decision Graph Intelligence</div>

                        <ul className="space-y-3 text-sm text-sand-50/60 font-mono">
                            <li className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-abyss-800 pb-2">
                                <span>Scale</span>
                                <span className="text-sand-50 sm:text-right">10,913 planning units · 97,573 graph nodes</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-abyss-800 pb-2">
                                <span>Governance</span>
                                <span className="text-sand-50 sm:text-right">24,316 governed claims · 514,728 relationships</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-abyss-800 pb-2">
                                <span>Screening Value</span>
                                <span className="text-sand-50 sm:text-right">Indicative ecosystem-service signal for screening</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-abyss-800 pb-2">
                                <span>Standard</span>
                                <span className="text-sand-50 sm:text-right">Screening-grade and bounded to prevent overclaiming</span>
                            </li>
                        </ul>

                        <div className="pt-4">
                            <a href="https://jaygut.github.io/blue-graph/" target="_blank" rel="noopener noreferrer" data-testid="bluecapital-bluegraph-link" className="inline-flex py-3 px-6 border border-teal-500/30 text-teal-400 rounded-sm text-xs uppercase tracking-widest hover:bg-teal-500/10 transition-colors items-center gap-2 group-hover:border-teal-500/50">
                                Open BlueGraph <ArrowUpRight size={12} />
                            </a>
                        </div>
                    </div>

                    <p className="text-sand-50/60 font-serif italic text-base pt-2">
                        Observations remain the reference against which the model is tested.
                    </p>
                    
                    {/* Visual Audit Trail / Data Lineage Enhancement */}
                    <div className="mt-8 pt-8 border-t border-abyss-800">
                        <h3 className="text-xs font-mono uppercase tracking-widest text-teal-400/60 mb-6 flex items-center gap-2">
                            <Activity size={14} />
                            Data Provenance Architecture
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <DataNode 
                                icon={Eye} 
                                title="1. Capture" 
                                desc="Satellite, Drone, eDNA, LTEM Diver Surveys" 
                            />
                            <DataNode 
                                icon={Database} 
                                title="2. Structure" 
                                desc="Standardization, Cleaning, Ontological Mapping" 
                            />
                            <DataNode 
                                icon={Shield} 
                                title="3. Verify" 
                                desc="Evidence Tiers, Uncertainty, Accountable Review" 
                            />
                        </div>
                    </div>

                </div>
            </div>

            {/* Proof Points */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">Proof Points</h2>
                </div>
                <div className="md:col-span-8 space-y-4">
                    <ProofPoint 
                        title="Governed Decision Graph"
                        stat="10,913"
                        label="Planning Units Governed"
                        desc="One inspectable reasoning graph linking place, evidence, and caveats to bounded, screening-grade decisions across 24,316 governed claims, published as public traces."
                    />
                    <ProofPoint 
                        title="Molecular Monitoring Protocol"
                        stat="eDNA"
                        label="Monitoring design"
                        desc="Protocol design for investigating biodiversity change in protected areas, subject to field validation."
                    />
                </div>
            </div>

            {/* The Larger Frame */}
            <div className="bg-abyss-900/30 border border-abyss-800 p-8 md:p-12 rounded-sm mt-12">
                <h3 className="text-2xl font-serif text-sand-50 mb-4">The Larger Frame</h3>
                <p className="text-sand-50/70 leading-relaxed font-light">
                    Blue natural capital extends beyond oceans. It proves that ecological complexity can be organized so that inspection preserves the living relationships. The same methods transfer directly to soil, forest and freshwater systems.
                </p>
            </div>

            <ReadingNext id="blue-capital" to="/systems#proof-bluegraph" title="Open the BlueGraph evidence record." copy="Trace a coastal question through the proof system, its provenance and its present boundary." />
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc }) => (
    <div className="p-6 bg-abyss-900/20 border border-abyss-800/50 hover:border-teal-500/30 transition-colors">
        <Icon size={20} className="text-teal-500 mb-3" />
        <h4 className="text-sand-50 font-medium mb-2">{title}</h4>
        <p className="text-sm text-sand-50/60 leading-relaxed">{desc}</p>
    </div>
);

const ProofPoint = ({ title, stat, label, desc }) => (
    <div data-testid={`blue-capital-proof-${stat.toLowerCase().replace(/[^a-z0-9]/g, '')}`} className="flex flex-col sm:flex-row gap-6 items-start p-6 hover:bg-white/5 transition-colors border-b border-abyss-800/50 last:border-0">
        <div className="text-center min-w-[100px]">
            <div className="text-3xl font-mono text-teal-400 font-light">{stat}</div>
            <div className="text-[10px] uppercase tracking-widest text-sand-50/40 mt-1">{label}</div>
        </div>
        <div>
            <h4 className="text-lg font-serif text-sand-50 mb-1">{title}</h4>
            <p className="text-sm text-sand-50/60 leading-relaxed">{desc}</p>
        </div>
    </div>
);

const DataNode = ({ icon: Icon, title, desc }) => (
    <div className="flex flex-col items-center text-center p-4 bg-abyss-900/30 border border-abyss-800 rounded-sm">
        <div className="mb-2 p-2 bg-abyss-800/50 rounded-full text-teal-400">
            <Icon size={16} />
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-sand-50/80 mb-1">{title}</div>
        <div className="text-[10px] text-sand-50/40 leading-tight">{desc}</div>
    </div>
);

export default BlueCapital;
